import type {
  WithContext,
  MedicalClinicLeaf,
  OpeningHoursSpecification,
  HospitalLeaf,
  DayOfWeek,
} from 'schema-dts';
import { extractNameOrValue } from 'lib/schema-builders/provider';
import { PageTemplateIds } from 'lib/constants';
import { Data } from '.generated/Foundation.CW.model';
import { Field } from '@sitecore-content-sdk/nextjs';

function getSchemaOpeningHours(
  officeHours?: Data.BaseTemplateSections.OfficeHours[]
): OpeningHoursSpecification[] | undefined {
  const hours = officeHours
    ?.map((item: Data.BaseTemplateSections.OfficeHours) => {
      let opens = item.fields?.open?.value;
      let closes = item.fields?.close?.value;

      // Handle 24-hour time specified as 0:00 or 00:00
      const is24Hours =
        (opens === '0:00' || opens === '00:00') && (closes === '0:00' || closes === '00:00');

      if (is24Hours) {
        opens = '00:00';
        closes = '23:59';
      }

      return {
        '@type': 'OpeningHoursSpecification' as const,
        opens,
        closes,
        dayOfWeek: item.fields?.officeHourType?.name as DayOfWeek,
      };
    })
    ?.filter((hour) => hour.opens && hour.closes && hour.dayOfWeek);

  if (!hours || hours.length === 0) return undefined;

  return hours;
}

export function buildLocationJsonLd(
  templateId: string,
  data: Data.BaseTemplateSections.Location,
  pageUrl: string
): WithContext<HospitalLeaf> | WithContext<MedicalClinicLeaf> {
  const isHospital = templateId === PageTemplateIds.locationHospital;
  const locFields = data.fields!;
  const street = extractNameOrValue(locFields.address1);
  const city = extractNameOrValue(locFields.city);
  const region = extractNameOrValue(locFields.state);
  const postalCode = extractNameOrValue(locFields.zip);

  const medicalSpecialties = locFields.medicalSpecialties as
    | { fields: { Value: Field<string> } }[]
    | undefined;

  const specialties = medicalSpecialties
    ?.map((s) => s?.fields?.Value?.value)
    .filter((s) => !!s)
    .map((s) => ({ '@type': 'MedicalSpecialty' as const, name: s }));

  const medSpecialties = specialties?.length
    ? specialties
    : [{ '@type': 'MedicalSpecialty' as const, name: 'Pediatric' }];

  const type = isHospital ? 'Hospital' : 'MedicalClinic';

  const schemaHours = getSchemaOpeningHours(locFields.officeHours);

  return {
    '@context': 'https://schema.org',
    '@type': type,
    name: locFields?.businessListingName?.value,
    url: pageUrl,
    id: pageUrl,
    image: locFields?.photo?.value?.src,
    telephone: locFields?.mobileVisiblePhoneNumber?.value,
    address: {
      '@type': 'PostalAddress',
      streetAddress: street,
      addressLocality: city,
      addressRegion: region,
      postalCode,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: data?.fields?.Latitude?.value,
      longitude: data?.fields?.Longitude?.value,
    },
    medicalSpecialty: medSpecialties,
    openingHoursSpecification: schemaHours,
  } as WithContext<HospitalLeaf> | WithContext<MedicalClinicLeaf>;
}
